import React, { useState, useEffect } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'

/**
 * CourtFormModal - Modal for creating or editing a court
 *
 * Props:
 * - show: boolean - controls modal visibility
 * - onHide: function - called when modal is closed
 * - onSave: function(courtData) - called when form is submitted
 * - court: object | null - existing court data for editing (null for new court)
 * - isProcessing: boolean - shows loading state during API call
 */
export default function CourtFormModal({
  show,
  onHide,
  onSave,
  court = null,
  isProcessing = false,
}) {
  const isEditMode = !!court

  const [formData, setFormData] = useState({
    name: '',
    address_line1: '',
    city: '',
    state: '',
    zip: '',
    lighting: false,
    surface: 'Hard',
    notes: '',
  })

  // Reset form when modal opens or court changes
  useEffect(() => {
    if (show) {
      if (court) {
        setFormData({
          name: court.name || '',
          address_line1: court.address_line1 || '',
          city: court.city || '',
          state: court.state || '',
          zip: court.zip || '',
          lighting: court.lighting || false,
          surface: court.surface || 'Hard',
          notes: court.notes || '',
        })
      } else {
        setFormData({
          name: '',
          address_line1: '',
          city: '',
          state: '',
          zip: '',
          lighting: false,
          surface: 'Hard',
          notes: '',
        })
      }
    }
  }, [show, court])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const dataToSave = isEditMode ? { ...formData, id: court.id } : formData
    onSave(dataToSave)
  }

  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.address_line1.trim() &&
      formData.city.trim() &&
      formData.state.trim() &&
      formData.zip.trim()
    )
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop={isProcessing ? 'static' : true}
    >
      <Modal.Header closeButton={!isProcessing}>
        <Modal.Title>{isEditMode ? 'Edit Court' : 'Add New Court'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Court Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Green Lake Tennis Courts"
              required
              disabled={isProcessing}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              placeholder="Street address"
              required
              disabled={isProcessing}
            />
          </Form.Group>

          <div className="row">
            <Form.Group className="col-12 col-md-6 mb-3">
              <Form.Label>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                required
                disabled={isProcessing}
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-3 mb-3">
              <Form.Label>
                State <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="WA"
                maxLength="2"
                pattern="[A-Z]{2}"
                title="Two-letter state code (e.g., WA, CA, NY)"
                required
                disabled={isProcessing}
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-3 mb-3">
              <Form.Label>
                ZIP <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                placeholder="98101"
                pattern="\d{5}(-\d{4})?"
                title="5-digit ZIP code (e.g., 98101) or ZIP+4 (e.g., 98101-1234)"
                required
                disabled={isProcessing}
              />
            </Form.Group>
          </div>

          <div className="row">
            <Form.Group className="col-12 col-md-6 mb-3">
              <Form.Label>Surface Type</Form.Label>
              <Form.Select
                name="surface"
                value={formData.surface}
                onChange={handleChange}
                disabled={isProcessing}
              >
                <option value="Hard">Hard</option>
                <option value="Clay">Clay</option>
                <option value="Grass">Grass</option>
                <option value="Carpet">Carpet</option>
                <option value="Unknown">Unknown</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="col-12 col-md-6 mb-3">
              <Form.Check
                type="checkbox"
                id="lighting-checkbox"
                name="lighting"
                label="Has Lighting"
                checked={formData.lighting}
                onChange={handleChange}
                disabled={isProcessing}
                className="mt-4"
              />
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Additional information about the court..."
              disabled={isProcessing}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isProcessing || !isFormValid()}
          >
            {isProcessing
              ? 'Saving...'
              : isEditMode
                ? 'Save Changes'
                : 'Add Court'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
